import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { OrganizationProvider } from './context/OrganizationContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { ActiveProjectProvider } from './context/ActiveProjectContext';
import ProtectedRoute from './routes/ProtectedRoute';
import AdminRoute     from './routes/AdminRoute';
import SuperAdminRoute from './routes/SuperAdminRoute';
import RoleRoute from './routes/RoleRoute';
import DefaultHomeRoute from './routes/DefaultHomeRoute';
import AdminLayout    from './admin/AdminLayout';
import AdminDashboard from './admin/pages/AdminDashboard';
import AdminUsers     from './admin/pages/AdminUsers';
import AdminLogs      from './admin/pages/AdminLogs';
import AdminSettings  from './admin/pages/AdminSettings';
import SuperAdminLayout from './superadmin/SuperAdminLayout';
import SuperAdminDashboard from './superadmin/pages/SuperAdminDashboard';
import SuperAdminUsers from './superadmin/pages/SuperAdminUsers';
import SuperAdminOrganizations from './superadmin/pages/SuperAdminOrganizations';
import SuperAdminAdmins from './superadmin/pages/SuperAdminAdmins';
import SuperAdminReports from './superadmin/pages/SuperAdminReports';
import SuperAdminSecurity from './superadmin/pages/SuperAdminSecurity';
import SuperAdminProjects from './superadmin/pages/SuperAdminProjects';
import SuperAdminTransactions from './superadmin/pages/SuperAdminTransactions';
import SuperAdminOnboarding from './superadmin/pages/SuperAdminOnboarding';
import LoginPage           from './pages/LoginPage';
import RegisterPage        from './pages/RegisterPage';
import ClientOverviewPage  from './pages/ClientOverviewPage';
import JoinOrganizationPage from './pages/JoinOrganizationPage';
import ForgotPasswordPage  from './pages/ForgotPasswordPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import MainDashboardPage   from './pages/MainDashboardPage';
import TransactionsPage from './pages/TransactionsPage';
import ProfilePage    from './pages/ProfilePage';
import CategoryPersonManagementPage from './pages/CategoryPersonManagementPage';
import HistoryAnalyticsPage from './pages/HistoryAnalyticsPage';
import OrganizationDashboardPage from './pages/OrganizationDashboardPage';
import OrganizationMembersPage from './pages/OrganizationMembersPage';
import OrganizationInvitationsPage from './pages/OrganizationInvitationsPage';
import OrganizationSettingsPage from './pages/OrganizationSettingsPage';
import OrganizationPermissionsPage from './pages/OrganizationPermissionsPage';
import ProjectPickerModal from './components/ProjectPickerModal';
import FinanceLayout from './layouts/FinanceLayout';
import AccountantLayout from './layouts/AccountantLayout';
import ProjectManagerLayout from './layouts/ProjectManagerLayout';
import ViewerLayout from './layouts/ViewerLayout';
import UserLayout from './layouts/UserLayout';
import FinanceDashboardPage from './pages/role/FinanceDashboardPage';
import AccountantDashboardPage from './pages/role/AccountantDashboardPage';
import ProjectManagerDashboardPage from './pages/role/ProjectManagerDashboardPage';
import ViewerDashboardPage from './pages/role/ViewerDashboardPage';
import EmployeeDashboardPage from './pages/role/EmployeeDashboardPage';
import RoleReadOnlyPage from './pages/role/RoleReadOnlyPage';
import PermissionDeniedToast from './components/PermissionDeniedToast';
import OrgAdminLayout from './layouts/OrgAdminLayout';
import OrgAdminDashboardPage from './pages/OrgAdminDashboardPage';
import OrgAdminMembersPage from './pages/OrgAdminMembersPage';
import OrgAdminPermissionsPage from './pages/OrgAdminPermissionsPage';
import OrgAdminInvitationsPage from './pages/OrgAdminInvitationsPage';
import OrgAdminSettingsPage from './pages/OrgAdminSettingsPage';
import OrgAdminLinkPage from './pages/OrgAdminLinkPage';
import { BarChart3, FileText, FolderKanban, History } from 'lucide-react';

function App() {
  return (
    <CurrencyProvider>
      <AuthProvider>
        <OrganizationProvider>
          <ActiveProjectProvider>
            <BrowserRouter>
            <Routes>
              {/* Public */}
              <Route path="/"               element={<ClientOverviewPage />} />
              <Route path="/login"           element={<LoginPage />} />
              <Route path="/register"        element={<RegisterPage />} />
              <Route path="/client-overview" element={<ClientOverviewPage />} />
              <Route path="/join"            element={<JoinOrganizationPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/access-denied"   element={<AccessDeniedPage />} />

              {/* Admin — role=admin required */}
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index          element={<AdminDashboard />} />
                  <Route path="users"   element={<AdminUsers />} />
                  <Route path="logs"    element={<AdminLogs />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>
              </Route>

              {/* Super Admin — highest privilege */}
              <Route element={<SuperAdminRoute />}>
                <Route path="/super-admin" element={<SuperAdminLayout />}>
                  <Route index element={<SuperAdminDashboard />} />
                  <Route path="users" element={<SuperAdminUsers />} />
                  <Route path="organizations" element={<SuperAdminOrganizations />} />
                  <Route path="onboarding" element={<SuperAdminOnboarding />} />
                  <Route path="projects" element={<SuperAdminProjects />} />
                  <Route path="transactions" element={<SuperAdminTransactions />} />
                  <Route path="admins" element={<SuperAdminAdmins />} />
                  <Route path="reports" element={<SuperAdminReports />} />
                  <Route path="security" element={<SuperAdminSecurity />} />
                </Route>
              </Route>

              {/* User — authenticated required */}
              <Route element={<ProtectedRoute />}>
                <Route element={<RoleRoute allowedRoles={['employee']} />}>
                  <Route path="/app" element={<UserLayout />}>
                    <Route index element={<EmployeeDashboardPage />} />
                  </Route>
                </Route>

                <Route element={<RoleRoute allowedRoles={['finance_manager']} />}>
                  <Route path="/finance" element={<FinanceLayout />}>
                    <Route index element={<FinanceDashboardPage />} />
                    <Route element={<RoleRoute allowedRoles={['finance_manager']} requiredPermissions={['view_reports']} />}>
                      <Route path="reports" element={<RoleReadOnlyPage title="Financial Reports" description="Review curated financial report views for your organization." />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['finance_manager']} requiredPermissions={['view_transactions']} />}>
                      <Route path="monitoring" element={<RoleReadOnlyPage title="Transaction Monitoring" description="Monitor organization transactions with finance-level visibility." />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['finance_manager']} requiredPermissions={['export_reports']} />}>
                      <Route path="exports" element={<RoleReadOnlyPage title="Export Reports" description="Prepare and export approved financial report datasets." />} />
                    </Route>
                  </Route>
                </Route>

                <Route element={<RoleRoute allowedRoles={['accountant']} />}>
                  <Route path="/accountant" element={<AccountantLayout />}>
                    <Route index element={<AccountantDashboardPage />} />
                    <Route element={<RoleRoute allowedRoles={['accountant']} requiredPermissions={['view_transactions']} />}>
                      <Route path="transactions" element={<RoleReadOnlyPage title="Account Transactions" description="Maintain daily accounting transaction entries." />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['accountant']} requiredPermissions={['manage_categories']} />}>
                      <Route path="categories" element={<RoleReadOnlyPage title="Category Management" description="Manage accounting categories used in transaction entries." />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['accountant']} requiredPermissions={['manage_persons']} />}>
                      <Route path="persons" element={<RoleReadOnlyPage title="Persons & Parties" description="Manage payer/payee person records for accounting flows." />} />
                    </Route>
                  </Route>
                </Route>

                <Route element={<RoleRoute allowedRoles={['project_manager']} />}>
                  <Route path="/project-manager" element={<ProjectManagerLayout />}>
                    <Route index element={<ProjectManagerDashboardPage />} />
                    <Route element={<RoleRoute allowedRoles={['project_manager']} requiredPermissions={['view_projects']} />}>
                      <Route path="projects" element={<RoleReadOnlyPage title="Assigned Projects" description="View and manage projects assigned to your role." />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['project_manager']} requiredPermissions={['view_project_history']} />}>
                      <Route path="history" element={<RoleReadOnlyPage title="Project History" description="Inspect project-level transaction and activity history." />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['project_manager']} requiredPermissions={['view_analytics']} />}>
                      <Route path="progress" element={<RoleReadOnlyPage title="Team Progress" description="Monitor team execution and project progress insights." />} />
                    </Route>
                  </Route>
                </Route>

                <Route element={<RoleRoute allowedRoles={['viewer']} />}>
                  <Route path="/viewer" element={<ViewerLayout />}>
                    <Route index element={<ViewerDashboardPage />} />
                    <Route element={<RoleRoute allowedRoles={['viewer']} requiredPermissions={['view_reports']} />}>
                      <Route path="reports" element={<RoleReadOnlyPage title="Read-only Reports" description="Access reports in read-only mode only." />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['viewer']} requiredPermissions={['view_dashboard_cards']} />}>
                      <Route path="overview" element={<RoleReadOnlyPage title="Allowed Overview" description="View high-level approved metrics and summaries." />} />
                    </Route>
                  </Route>
                </Route>

                <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} />}>
                  <Route path="/org-admin" element={<OrgAdminLayout />}>
                    <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} requiredPermissions={['view_dashboard_cards']} />}>
                      <Route index element={<OrgAdminDashboardPage />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} requiredPermissions={['view_users']} />}>
                      <Route path="members" element={<OrgAdminMembersPage />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} requiredPermissions={['manage_role_permissions']} />}>
                      <Route path="permissions" element={<OrgAdminPermissionsPage />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} requiredPermissions={['view_users', 'invite_users']} />}>
                      <Route path="invitations" element={<OrgAdminInvitationsPage />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} requiredPermissions={['manage_organization_settings']} />}>
                      <Route path="settings" element={<OrgAdminSettingsPage />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} requiredPermissions={['view_transactions']} />}>
                      <Route path="transactions" element={<OrgAdminLinkPage title="Transactions" description="Manage all financial transaction entries for your organization." Icon={FileText} targetPath="/transactions" linkLabel="Open Transactions" />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} requiredPermissions={['view_projects']} />}>
                      <Route path="projects" element={<OrgAdminLinkPage title="Projects" description="View and manage all active projects in your organization." Icon={FolderKanban} targetPath="/dashboard" linkLabel="Open Projects" />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} requiredPermissions={['view_analytics', 'view_reports']} />}>
                      <Route path="analytics" element={<OrgAdminLinkPage title="Analytics & Reports" description="Review financial trends, monthly reports, and performance data." Icon={BarChart3} targetPath="/history-analytics" linkLabel="Open Analytics" />} />
                    </Route>
                    <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} requiredPermissions={['view_project_history']} />}>
                      <Route path="history" element={<OrgAdminLinkPage title="Transaction History" description="Inspect project-level transaction history and audit trails." Icon={History} targetPath="/history-analytics" linkLabel="Open History" />} />
                    </Route>
                  </Route>
                </Route>

                <Route element={<RoleRoute allowedRoles={['organization_head', 'admin', 'project_manager', 'employee']} />}>
                  <Route path="/dashboard" element={<MainDashboardPage />} />
                </Route>
                <Route path="/overview"     element={<Navigate to="/dashboard" replace />} />
                <Route element={<RoleRoute allowedRoles={['organization_head', 'admin', 'finance_manager', 'accountant', 'project_manager', 'employee']} />}>
                  <Route path="/transactions" element={<TransactionsPage />} />
                </Route>
                <Route element={<RoleRoute allowedRoles={['organization_head', 'admin', 'finance_manager', 'accountant', 'project_manager', 'employee', 'viewer']} />}>
                  <Route path="organization/dashboard" element={<OrganizationDashboardPage />} />
                </Route>
                <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} />}>
                  <Route path="organization/members" element={<OrganizationMembersPage />} />
                </Route>
                <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} />}>
                  <Route path="organization/invitations" element={<OrganizationInvitationsPage />} />
                </Route>
                <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} />}>
                  <Route path="organization/settings" element={<OrganizationSettingsPage />} />
                </Route>
                <Route element={<RoleRoute allowedRoles={['organization_head', 'admin']} requiredPermissions={['manage_role_permissions']} />}>
                  <Route path="organization/permissions" element={<OrganizationPermissionsPage />} />
                </Route>
                <Route element={<RoleRoute allowedRoles={['organization_head', 'admin', 'accountant']} />}>
                  <Route path="/management" element={<CategoryPersonManagementPage />} />
                </Route>
                <Route element={<RoleRoute allowedRoles={['organization_head', 'admin', 'finance_manager', 'project_manager', 'employee', 'viewer']} />}>
                  <Route path="/history-analytics" element={<HistoryAnalyticsPage />} />
                </Route>
                <Route path="/profile"      element={<ProfilePage />} />
              </Route>

              <Route path="*" element={<DefaultHomeRoute />} />
            </Routes>
            <ProjectPickerModal />
            <PermissionDeniedToast />
            </BrowserRouter>
          </ActiveProjectProvider>
        </OrganizationProvider>
      </AuthProvider>
    </CurrencyProvider>
  );
}

export default App;
